import { useEffect, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { getOrgConfigPortal, patchOrgConfig } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useParams } from "react-router";

import { REPORT_LANGUAGES_MAP } from "@/common/constants/languages";

const SupportedLanguages = () => {
  const { orgId } = useParams();
  const [serviceConfig, setServiceConfig] = useState<any>(null);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [isSavingLanguages, setIsSavingLanguages] = useState(false);

  useEffect(() => {
    async function getConfig() {
      try {
        const response = await getOrgConfigPortal({
          path: { org_id: orgId ?? "" },
        });
        if (response.data) {
          setServiceConfig(response.data);
          // Initialize selected languages from serviceConfig
          const supportedLanguages =
            (response.data.supported_languages as
              | string[]
              | null
              | undefined) ?? [];
          if (supportedLanguages.length > 0) {
            // Normalize case: backend may return "enCA" but we use "enCa" (camelCase)
            const normalizedLanguages = supportedLanguages.map((lang) => {
              if (lang === "enCA") return "enCa";
              if (lang === "enUS") return "enUs";
              if (lang === "frCA") return "frCa";
              return lang;
            });
            // Remove duplicates
            const uniqueLanguages = Array.from(new Set(normalizedLanguages));
            setSelectedLanguages(uniqueLanguages);
          } else {
            // Default to enCa and fr if not set
            setSelectedLanguages(["enCa", "fr"]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch org config:", error);
        setSelectedLanguages(["enCa", "fr"]);
        setServiceConfig({ supported_languages: ["enCa", "fr"] });
      }
    }
    getConfig();
  }, [orgId]);

  const handleSaveLanguages = async () => {
    if (!serviceConfig) return;

    if (selectedLanguages.length === 0) {
      toast.error("At least one language must be selected");
      return;
    }

    setIsSavingLanguages(true);
    try {
      const newConfig = (
        await patchOrgConfig({
          path: { org_id: orgId ?? "" },
          body: {
            supported_languages: selectedLanguages,
          },
        })
      ).data!;
      setServiceConfig(newConfig);
      toast.success("Supported languages updated successfully");
    } catch (error) {
      toast.error("Failed to update supported languages");
      console.error("Error updating supported languages:", error);
    } finally {
      setIsSavingLanguages(false);
    }
  };

  if (!serviceConfig) {
    return <LinearProgress sx={{ width: "100%" }} />;
  }

  const isError = selectedLanguages.length === 0;
  const selectedSet = new Set(selectedLanguages);
  const hasEnCa = selectedSet.has("enCa");
  const hasEnUs = selectedSet.has("enUs");
  const hasFr = selectedSet.has("fr");
  const hasFrCa = selectedSet.has("frCa");

  const selectedOptions = REPORT_LANGUAGES_MAP.filter((l) =>
    selectedSet.has(l.code),
  );

  const handleSelectAll = () => {
    // Select all languages, but only one of enCa/enUs (prefer enCa) and one of fr/frCa (prefer frCa)
    const allCodes = REPORT_LANGUAGES_MAP.map((lang) => lang.code);
    const filteredCodes = allCodes.filter((code) => {
      // Only include one of enCa/enUs
      if (code === "enUs") return false; // Prefer enCa over enUs
      // Only include one of fr/frCa
      if (code === "fr") return false; // Prefer frCa over fr
      return true;
    });
    setSelectedLanguages(filteredCodes);
  };

  const allSelected =
    selectedLanguages.length ===
    REPORT_LANGUAGES_MAP.filter(
      (lang) => lang.code !== "enUs" && lang.code !== "fr", // Exclude enUs and fr since we prefer enCa and frCa
    ).length;

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={700}>
        Supported Languages
      </Typography>

      <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
        Select the languages that will be available for translation in your
        agency&apos;s online reporting forms. Users will only see these
        languages in the language selector.
      </Typography>

      <FormControl fullWidth error={isError}>
        <Box sx={{ mb: 1, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={allSelected}
            sx={{ textTransform: "none" }}
          >
            {allSelected ? "All Selected" : "Select All"}
          </Button>
        </Box>
        <Autocomplete
          multiple
          options={REPORT_LANGUAGES_MAP}
          value={selectedOptions}
          filterSelectedOptions
          disableCloseOnSelect
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(o, v) => o.code === v.code}
          getOptionDisabled={(option) =>
            (hasEnCa && option.code === "enUs") ||
            (hasEnUs && option.code === "enCa") ||
            (hasFr && option.code === "frCa") ||
            (hasFrCa && option.code === "fr")
          }
          onChange={(_, newValue) => {
            let codes = newValue.map((v) => v.code);

            // Safety net: enforce mutual exclusivity for English variants
            const hasCa = codes.includes("enCa");
            const hasUs = codes.includes("enUs");
            if (hasCa && hasUs) {
              // Deterministic: keep the last one selected
              const last = codes[codes.length - 1];
              codes = codes.filter(
                (c) => c !== (last === "enCa" ? "enUs" : "enCa"),
              );
            }

            // Safety net: enforce mutual exclusivity for French variants
            const hasFrench = codes.includes("fr");
            const hasFrenchCa = codes.includes("frCa");
            if (hasFrench && hasFrenchCa) {
              // Deterministic: keep the last one selected
              const last = codes[codes.length - 1];
              codes = codes.filter(
                (c) => c !== (last === "fr" ? "frCa" : "fr"),
              );
            }

            setSelectedLanguages(codes);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Supported Languages"
              placeholder="Select languages…"
              helperText="Select all languages that should be available for your agency"
              error={isError}
            />
          )}
          renderValue={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.name}
                  size="small"
                  {...tagProps}
                />
              );
            })
          }
          sx={{ width: "100%" }}
        />
        <FormHelperText>
          {isError
            ? "At least one language must be selected"
            : `${selectedLanguages.length} language${
                selectedLanguages.length !== 1 ? "s" : ""
              } selected`}
        </FormHelperText>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveLanguages}
            disabled={isSavingLanguages || isError}
          >
            {isSavingLanguages ? "Saving..." : "Save Languages"}
          </Button>
        </Box>
      </FormControl>
    </Paper>
  );
};

export default SupportedLanguages;
